import styled from "styled-components";
import { QUERIES, SPACING } from "../../../../../utils/constants";
import { StatTone, SwatchVariant } from "./types";

const SWATCH_COLOR: Record<SwatchVariant, string> = {
  repaid: "var(--color-primary)",
  owed: "color-mix(in srgb, var(--color-primary) 22%, transparent)",
  interest: "var(--color-error)",
  paid: "var(--color-text-muted)",
  borrowed: "color-mix(in srgb, var(--color-text-muted) 50%, transparent)",
};

const TONE_COLOR: Record<StatTone, string> = {
  default: "var(--color-text-default)",
  primary: "var(--color-primary)",
  error: "var(--color-error)",
  muted: "var(--color-text-secondary)",
};

export const BreakdownCard = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px;
  min-width: 0;
  background: var(--color-card-background);
  border: 1px solid var(--color-card-border);
  border-radius: 16px;
  box-shadow: var(--shadow-small);

  @media ${QUERIES.tabletLargeAndUp} {
    padding: ${SPACING.lg}px;
  }
`;

export const BreakdownHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const BreakdownTitle = styled.h2`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xxl);
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.1;
  color: var(--color-text-default);
  margin: 0;
`;

export const RepaidTag = styled.span`
  font-size: var(--text-sm);
  font-style: italic;
  color: var(--color-text-secondary);
  white-space: nowrap;

  @media ${QUERIES.laptopAndUp} {
    font-size: var(--text-base);
  }
`;

export const ProgressTrack = styled.div`
  position: relative;
  height: 14px;
  border-radius: 999px;
  background-color: var(--color-border);
  overflow: hidden;

  @media ${QUERIES.laptopAndUp} {
    display: none;
  }
`;

export const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => `${$pct}%`};
  border-radius: 999px;
  background-color: var(--color-primary);
`;

export const StatColumns = styled.div`
  display: none;

  @media ${QUERIES.laptopAndUp} {
    display: flex;
    align-items: flex-start;
    gap: ${SPACING.lg}px;
    flex-wrap: wrap;
  }
`;

export const StatDivider = styled.div`
  align-self: stretch;
  width: 1px;
  background: var(--color-border);
`;

export const StatColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
  flex: 1 1 0;
  min-width: 110px;
`;

export const StatColumnLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-secondary);
`;

export const StatColumnValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
`;

export const Swatch = styled.span<{ $variant: SwatchVariant }>`
  width: 12px;
  height: 12px;
  border-radius: 4px;
  flex-shrink: 0;
  background: ${({ $variant }) => SWATCH_COLOR[$variant]};
`;

export const StatColumnValue = styled.span<{ $tone: StatTone }>`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xxl);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  white-space: nowrap;
  color: ${({ $tone }) => TONE_COLOR[$tone]};
`;

export const StatList = styled.div`
  display: flex;
  flex-direction: column;

  @media ${QUERIES.laptopAndUp} {
    display: none;
  }
`;

export const StatRowItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px 0;

  & + & {
    border-top: 1px solid var(--color-border);
  }
`;

export const StatRowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  min-width: 0;
`;

export const Dot = styled.span<{ $variant: SwatchVariant }>`
  width: 14px;
  height: 14px;
  border-radius: 5px;
  flex-shrink: 0;
  background: ${({ $variant }) => SWATCH_COLOR[$variant]};
`;

export const StatRowLabel = styled.span`
  font-size: var(--text-base);
  color: var(--color-text-default);
`;

export const StatRowValue = styled.span<{ $tone: StatTone }>`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  font-weight: 700;
  letter-spacing: -0.01em;
  white-space: nowrap;
  color: ${({ $tone }) => TONE_COLOR[$tone]};
`;

export const BreakdownNote = styled.p`
  margin: 0;
  font-size: var(--text-sm);
  font-style: italic;
  line-height: 1.5;
  color: var(--color-text-secondary);

  @media ${QUERIES.laptopAndUp} {
    font-size: var(--text-base);
  }
`;

export const NotePrefix = styled.span`
  display: none;

  @media ${QUERIES.laptopAndUp} {
    display: inline;
  }
`;

export const DetailsDivider = styled.div`
  display: none;

  @media ${QUERIES.laptopAndUp} {
    display: block;
    height: 1px;
    background: var(--color-border);
  }
`;

export const DetailRow = styled.div`
  display: none;

  @media ${QUERIES.laptopAndUp} {
    display: flex;
    gap: ${SPACING.lg}px;
    flex-wrap: wrap;
  }
`;

export const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
  flex: 1 1 0;
  min-width: 120px;
`;

export const DetailItemLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-secondary);
`;

export const DetailItemValue = styled.span<{ $accent?: boolean }>`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-lg);
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ $accent }) =>
    $accent ? "var(--color-primary)" : "var(--color-text-default)"};
  word-break: break-word;
`;

export const DetailItemSub = styled.span`
  font-size: var(--text-xs);
  font-style: italic;
  color: var(--color-text-secondary);
`;
